import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingFocusManager,
  FloatingOverlay,
  useId,
  useTransitionStyles,
} from '@floating-ui/react';
import clsx from 'clsx';
import * as React from 'react';
import { useMediaQuery } from '../hooks/use-media-query';

export function useDialog({
  initialOpen = false,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  className,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState();
  const [descriptionId, setDescriptionId] = React.useState();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    open,
    onOpenChange: setOpen,
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      className,
    }),
    [open, setOpen, interactions, data, labelId, descriptionId, className],
  );
}

const DialogContext = React.createContext(null);

export const useDialogContext = () => {
  const context = React.useContext(DialogContext);

  if (context == null) {
    throw new Error('Dialog components must be wrapped in <Dialog />');
  }

  return context;
};

export function Dialog({ children, ...options }) {
  const dialog = useDialog(options);
  return <DialogContext.Provider value={dialog}>{children}</DialogContext.Provider>;
}

export const DialogTrigger = React.forwardRef(function DialogTrigger(
  { children, asChild = false, ...props },
  propRef,
) {
  const context = useDialogContext();
  const childrenRef = ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <button
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const DialogContent = React.forwardRef(function DialogContent(
  { direction = 'auto', ...props },
  propRef,
) {
  const { context: floatingContext, ...context } = useDialogContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  const isDesktop = useMediaQuery('(min-width: 640px)');
  const effectiveDirection = direction === 'auto' ? (isDesktop ? 'left' : 'right') : direction;

  const transforms = {
    left: {
      initial: 'translateX(-100%)',
      open: 'translateX(0)',
      close: 'translateX(-100%)',
    },
    right: {
      initial: 'translateX(100%)',
      open: 'translateX(0)',
      close: 'translateX(100%)',
    },
    bottom: {
      initial: 'translateY(100%)',
      open: 'translateY(0)',
      close: 'translateY(100%)',
    },
  };

  const { isMounted, styles } = useTransitionStyles(floatingContext, {
    duration: 700,
    easing: 'cubic-bezier(.4,0,.2,1)',
    initial: {
      opacity: direction === 'auto' ? 1 : 0,
      transform: transforms[effectiveDirection].initial,
    },
    open: {
      opacity: 1,
      transform: transforms[effectiveDirection].open,
    },
    close: {
      opacity: direction === 'auto' ? 1 : 0,
      transform: transforms[effectiveDirection].close,
    },
  });

  const { styles: overlayStyles } = useTransitionStyles(floatingContext, {
    duration: 700,
    easing: 'cubic-bezier(.4,0,.2,1)',
    initial: { backgroundColor: 'rgba(0,0,0,0)' },
    open: { backgroundColor: 'rgba(17,24,39,0.75)' },
    close: { backgroundColor: 'rgba(0,0,0,0)' },
  });

  if (!isMounted) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay
        style={isDesktop ? overlayStyles : null}
        className={clsx(
          'size-full z-100 flex justify-center items-center !overflow-hidden',
          context?.className,
        )}
        lockScroll
      >
        <FloatingFocusManager context={floatingContext}>
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            style={styles}
            aria-labelledby={context.labelId}
            aria-describedby={context.descriptionId}
            {...context.getFloatingProps(props)}
          >
            {props.children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
});

export const DialogHeading = React.forwardRef(function DialogHeading({ children, ...props }, ref) {
  const { setLabelId } = useDialogContext();
  const id = useId();

  // Only sets `aria-labelledby` on the Dialog root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setLabelId(id);
    return () => setLabelId(undefined);
  }, [id, setLabelId]);

  return (
    <h2 {...props} ref={ref} id={id}>
      {children}
    </h2>
  );
});

export const DialogDescription = React.forwardRef(function DialogDescription(
  { children, ...props },
  ref,
) {
  const { setDescriptionId } = useDialogContext();
  const id = useId();

  // Only sets `aria-describedby` on the Dialog root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(undefined);
  }, [id, setDescriptionId]);

  return (
    <p {...props} ref={ref} id={id}>
      {children}
    </p>
  );
});

export const DialogClose = React.forwardRef(function DialogClose(props, ref) {
  const { setOpen } = useDialogContext();
  return <button type="button" {...props} ref={ref} onClick={() => setOpen(false)} />;
});
